import * as React from 'react';
import { observer } from 'mobx-react';
import { ProrationRuleTabView } from 'nomst/Stores/Budget/prorationRuleTabView';
import { ProrationRule } from 'nomst/Stores/Configuration/prorationRule';
import ProrationRuleCard from 'nomst/Components/EligibilityProration/prorationRuleCard';
import { DeleteEligibilityRuleModal } from 'nomst/Components/EligibilityProration/deleteEligibilityRuleModal';
import Loading from 'components/Common/loading';
import './prorationRules.scss';

export interface IProrationRulesProps {
    prorationRuleTabView: ProrationRuleTabView;
}

interface IProrationRulesState {
    showDeleteModal: boolean;
    ruleToDelete: Nullable<ProrationRule>;
}

export class ProrationRules extends React.Component<IProrationRulesProps, IProrationRulesState> {
    
    constructor(props: IProrationRulesProps) {
        super(props);
        this.handleAddRuleClick = this.handleAddRuleClick.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleDeleteModalClose = this.handleDeleteModalClose.bind(this);
        this.renderRule = this.renderRule.bind(this);
        this.handleDeleteRule = this.handleDeleteRule.bind(this);

        this.state = {
            showDeleteModal: false,
            ruleToDelete: null
        };
    }

    componentDidMount(): Promise<void> {
        const { prorationRuleTabView } = this.props;
        return prorationRuleTabView.fetchRules();
    }

    render(): React.ReactNode {
        const { prorationRuleTabView } = this.props;
        const { showDeleteModal } = this.state;
        if (prorationRuleTabView.isLoading) {
            return (
                <Loading className="proration-rules proration-rules--loading" />
            );
        }

        if (!prorationRuleTabView.doesHaveBudget ||
            prorationRuleTabView.currentConfiguration.teamPageViews.preFillDecision) {
            return this.renderNoBudget();
        }

        if (!prorationRuleTabView.prorationRules.length) {
            return this.renderNoRules();
        }

        return (
            <div className="proration-rules">
                {prorationRuleTabView.prorationRules.map(this.renderRule)}
                {!prorationRuleTabView.anyRulesInEditMode &&
                <button
                    className="pxl-link proration-rules__add-rule"
                    type="button"
                    onClick={this.handleAddRuleClick}
                >
                    <i className="icon-border icon-circle-thin">
                        <i className="icon-plus" />
                    </i>
                    <span>Add Proration Rule</span>
                </button>}
                {showDeleteModal && (
                    <DeleteEligibilityRuleModal
                        onClose={this.handleDeleteModalClose}
                        onConfirm={this.handleDeleteRule}
                        isDeleting={prorationRuleTabView.isLoading}
                        isProration={true}
                    />
                )}
            </div>
        );
    }

    private renderNoBudget(): React.ReactNode {
        return (
            <div className="proration-rules proration-rules--no-budget">
                <img
                    alt=""
                    src={require('contents/eligibility_no_budget.svg')}
                    className="proration-rules__no-budget-image"
                />
                <p>
                    In order to add proration rules, please create budget first
                </p>
                <p className="proration-rules__no-budget-description">
                    Before creating a proration rule, ensure pre-fill decision fields
                    is turned off in the cycle settings
                </p>
            </div>
        );
    }

    private renderNoRules(): React.ReactNode {
        return (
            <div className="proration-rules proration-rules--no-rules">
                <p>
                    Proration rules allow you to calculate the correct prorated payout based on the period of time
                    employees have been with your org or based on their last pay change.
                </p>
                <button
                    className="pxl-btn pxl-btn--primary proration-rules__add-btn"
                    type="button"
                    onClick={this.handleAddRuleClick}
                >
                    Add Proration Rule
                </button>
            </div>
        );
    }

    private renderRule(rule: ProrationRule, index: number): React.ReactNode {
        const { removeRule, updateAssociatedAmounts } = this.props.prorationRuleTabView;
        return (
            <ProrationRuleCard
                key={index}
                rule={rule}
                removeRule={removeRule}
                handleDeleteClick={this.handleDeleteClick}
                updateOtherRuleAmounts={updateAssociatedAmounts}
            />
        );
    }

    private handleAddRuleClick(): void {
        const { prorationRuleTabView } = this.props;
        prorationRuleTabView.addNewRule();
    }

    private handleDeleteClick(rule: ProrationRule): void {
        this.setState({
            showDeleteModal: true,
            ruleToDelete: rule
        });
    }

    private handleDeleteModalClose(): void {
        this.setState({
            showDeleteModal: false,
            ruleToDelete: null
        });
    }

    private async handleDeleteRule(): Promise<void> {
        const { ruleToDelete } = this.state;
        if (ruleToDelete) {
            await this.props.prorationRuleTabView.deleteRule(ruleToDelete);
            this.setState({
                showDeleteModal: false,
                ruleToDelete: null
            });
        }
    }
}

export default observer(ProrationRules);